import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ClarityModule } from '@clr/angular';
import { Observable, Subscription, finalize, interval } from 'rxjs';
import { HasPermissionDirective } from '../../../../../security/has-permission.directive';
import {
  EnvironmentFleetService,
  FleetActionResult,
  FleetInstance,
  FleetInstanceClass,
  FleetStatus,
} from '../../services/environment-fleet.service';

type FleetVerb = 'activate' | 'hibernate' | 'deactivate';

interface InstanceGroup {
  key: FleetInstanceClass;
  title: string;
  hint: string;
  instances: FleetInstance[];
}

const POLL_MS = 10_000;

/**
 * 2026-09-07 — Umgebungssteuerung (Aktivieren / Ruhezustand / Deaktivieren).
 *
 * Steuert bewusst nur die EIGENE Umgebung: dev, stg und prd liegen in
 * getrennten AWS-Konten, und jedes Portal hat eine Rolle nur in seinem eigenen.
 * Die Stage kommt vom Backend, nicht aus der UI — es gibt hier keinen Weg, aus
 * Versehen eine andere Umgebung zu treffen.
 *
 * "Deaktivieren" terminiert nur, was das Portal selbst wieder aufbauen kann
 * (Voxtral-Realtime, Whisper transcribe-live, ASG-Kapazität). Die dauerhaften
 * Instanzen werden auch dabei nur gestoppt — ein Terminate ließe sich nur mit
 * `tofu apply` rückgängig machen.
 */
@Component({
  selector: 'app-environment-control-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, ClarityModule, HasPermissionDirective],
  templateUrl: './environment-control-panel.component.html',
  styleUrl: './environment-control-panel.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class EnvironmentControlPanelComponent implements OnDestroy {

  @Input() set active(value: boolean) {
    this.isActive = value;
    if (value) {
      if (!this.status && !this.loading) this.refresh();
      this.startPolling();
    } else {
      this.stopPolling();
    }
  }

  status: FleetStatus | null = null;
  loading = false;
  error: string | null = null;

  /** Läuft gerade eine Aktion — sperrt alle drei Knöpfe. */
  running: FleetVerb | null = null;
  lastResult: FleetActionResult | null = null;

  /** 409 vom Backend: Live-Transkription läuft noch. */
  busyWarning: string | null = null;
  pendingVerb: FleetVerb | null = null;

  deactivateDialogOpen = false;
  confirmText = '';

  private isActive = false;
  private poll?: Subscription;

  constructor(private fleet: EnvironmentFleetService) {}

  ngOnDestroy(): void {
    this.stopPolling();
  }

  // ---- Status ------------------------------------------------------------

  refresh(): void {
    this.loading = true;
    this.error = null;
    this.fleet.getStatus()
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: status => this.status = status,
        error: err => this.error = this.messageOf(err),
      });
  }

  get stage(): string {
    return (this.status?.stage ?? '').toUpperCase();
  }

  get groups(): InstanceGroup[] {
    const instances = this.status?.instances ?? [];
    const defs: { key: FleetInstanceClass; title: string; hint: string }[] = [
      {
        key: 'Permanent',
        title: 'Dauerhafte Dienste',
        hint: 'Werden gestoppt, nie terminiert — ein Neuaufbau bräuchte `tofu apply`.',
      },
      {
        key: 'AutoScaled',
        title: 'Auto-Scaling (transcribe-vod)',
        hint: 'Wird über die Gruppenkapazität gesteuert, nicht pro Instanz.',
      },
      {
        key: 'VoxtralRealtime',
        title: 'Voxtral Realtime (GPU)',
        hint: 'Startet der Scheduler pro Sendung; „Aktivieren" lässt sie geparkt.',
      },
      {
        key: 'WhisperLive',
        title: 'Whisper transcribe-live (GPU)',
        hint: 'Pro Sendung erzeugt; kennt keinen gestoppten Zustand.',
      },
    ];

    return defs
      .map(d => ({ ...d, instances: instances.filter(i => i.class === d.key) }))
      .filter(g => g.instances.length > 0);
  }

  /**
   * 2026-09-09 — the non-EC2 half of the environment, so a Ruhezustand can be
   * seen to have reached past the instances. Counts rather than full lists: the
   * operator wants "is it down", and 16 Lambda rows would bury the EC2 table.
   */
  get serverlessSummary(): { label: string; done: number; total: number }[] {
    const s = this.status?.serverless;
    if (!s) return [];
    return [
      {
        label: 'Lambdas gedrosselt',
        done: s.functions.filter(f => f.throttled).length,
        total: s.functions.length,
      },
      {
        label: 'Trigger deaktiviert',
        done: s.triggers.filter(t => !t.enabled).length,
        total: s.triggers.length,
      },
      {
        label: 'ECS-Tasks gestoppt',
        done: s.tasks.filter(t => t.lastStatus === 'STOPPED').length,
        total: s.tasks.length,
      },
    ];
  }

  /** Tasks still running — these block Ruhezustand/Deaktivieren without force. */
  get runningTasks(): { cluster: string; family: string; lastStatus: string }[] {
    return (this.status?.serverless?.tasks ?? []).filter(t => t.lastStatus !== 'STOPPED');
  }

  targetLabel(target: string): string {
    switch (target) {
      case 'hibernate': return 'Ruhezustand';
      case 'deactivate': return 'Deaktiviert';
      default: return 'Aktiv';
    }
  }

  get jobsPaused(): boolean {
    return this.status?.jobsPaused === true;
  }

  get liveCount(): number {
    return (this.status?.instances ?? []).filter(
      i => (i.state === 'running' || i.state === 'pending')
        && (i.class === 'WhisperLive' || (i.class === 'VoxtralRealtime' && i.role === 'active'))
    ).length;
  }

  stateLabel(state: string): string {
    switch (state) {
      case 'running': return 'läuft';
      case 'pending': return 'startet';
      case 'stopping': return 'stoppt';
      case 'stopped': return 'gestoppt';
      case 'shutting-down': return 'wird beendet';
      default: return state;
    }
  }

  stateClass(state: string): string {
    switch (state) {
      case 'running': return 'label-success';
      case 'pending':
      case 'stopping':
      case 'shutting-down': return 'label-warning';
      case 'stopped': return 'label-light';
      default: return 'label-light';
    }
  }

  // ---- Aktionen ----------------------------------------------------------

  activate(): void {
    this.run('activate', () => this.fleet.activate());
  }

  hibernate(force = false): void {
    this.run('hibernate', () => this.fleet.hibernate(force));
  }

  openDeactivateDialog(): void {
    this.confirmText = '';
    this.busyWarning = null;
    this.deactivateDialogOpen = true;
  }

  confirmDeactivate(force = false): void {
    if (!this.confirmMatches) return;
    this.run('deactivate', () => this.fleet.deactivate(this.confirmText.trim(), force), () => {
      this.deactivateDialogOpen = false;
      this.confirmText = '';
    });
  }

  get confirmMatches(): boolean {
    return this.confirmText.trim().toLowerCase() === (this.status?.stage ?? '').toLowerCase()
      && this.confirmText.trim().length > 0;
  }

  /** Wiederholt die zuletzt am 409 gescheiterte Aktion mit force. */
  forceRetry(): void {
    const verb = this.pendingVerb;
    this.busyWarning = null;
    this.pendingVerb = null;
    if (verb === 'hibernate') this.hibernate(true);
    if (verb === 'deactivate') this.confirmDeactivate(true);
  }

  dismissBusyWarning(): void {
    this.busyWarning = null;
    this.pendingVerb = null;
  }

  private run(
    verb: FleetVerb,
    call: () => Observable<FleetActionResult>,
    onSuccess?: () => void
  ): void {
    this.running = verb;
    this.error = null;
    this.busyWarning = null;
    this.lastResult = null;

    call()
      .pipe(finalize(() => this.running = null))
      .subscribe({
        next: result => {
          this.lastResult = result;
          onSuccess?.();
          this.refresh();
        },
        error: err => {
          const e = err as { status?: number; error?: { error?: string; canForce?: boolean } };
          if (e?.status === 409 && e.error?.canForce) {
            // Live-Transkription läuft noch — der Operator entscheidet.
            this.busyWarning = e.error.error ?? 'Es läuft noch eine Live-Transkription.';
            this.pendingVerb = verb;
            return;
          }
          this.error = this.messageOf(err);
        },
      });
  }

  // ---- Polling -----------------------------------------------------------

  private startPolling(): void {
    if (this.poll) return;
    this.poll = interval(POLL_MS).subscribe(() => {
      if (this.isActive && !this.running && !this.loading) this.refresh();
    });
  }

  private stopPolling(): void {
    this.poll?.unsubscribe();
    this.poll = undefined;
  }

  private messageOf(err: unknown): string {
    const e = err as { error?: unknown; message?: string };
    if (typeof e?.error === 'string' && e.error) return e.error;
    const nested = (e?.error as { error?: string })?.error;
    if (typeof nested === 'string' && nested) return nested;
    return e?.message ?? 'Unbekannter Fehler';
  }
}
